import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FolderOpen, Truck, FileCheck, Package, AlertTriangle, AlertCircle, Warehouse, ArrowRight, ShoppingCart, ChevronDown, Clock, Settings2, Download, Sparkles, X } from 'lucide-react';
import { KPITile } from '../shared/components/KPITile.js';
import { Card } from '../shared/components/Card.js';
import { DataTable, type Column } from '../shared/components/DataTable.js';
import { useAuth } from '../auth/AuthContext.js';
import { isOwnerRole } from '../auth/roles.js';
import { apiFetch } from '../shared/hooks/useApi.js';
import { formatDatum } from '../shared/utils/formatters.js';
import clsx from 'clsx';
import { AlertPanel } from '../../modules/ixina-dashboard/components/AlertPanel.js';
import { WidgetConfigModal } from '../../modules/ixina-dashboard/components/WidgetConfigModal.js';
import { useDashboardConfig } from '../../modules/ixina-dashboard/hooks/useDashboard.js';
import { useDashboardSocket } from '../../modules/ixina-dashboard/hooks/useDashboardSocket.js';
import { BriefingCard } from '../../modules/ixina-dashboard/components/BriefingCard.js';

function parseNaam(naam: string): string {
  try { return JSON.parse(naam).nl; } catch { return naam; }
}

function formatLeverDatum(date: string | null): string {
  if (!date) return '—';
  return new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(date));
}

interface Collega {
  id: string;
  firstName: string;
  lastName: string;
}

interface DashboardData {
  kpis: { actieveDossiers: number; leveringenDezeWeek: number; openVorderingsstaten: number; apparatenTeLaat: number };
  alerts: { id: string; type: string; dossierNummer: string; klantNaam: string; dagenTotLevering: number | null; reden: string }[];
  dossiers: {
    id: string; dossierNummer: string; klantNaam: string; status: string; leverDatum: string | null;
    trajectstappen: any[]; apparatenBinnen: number; apparatenTotaal: number; vorderingsstaatStatus: string | null;
  }[];
  collega: Collega[];
}

const statusBadge: Record<string, { bg: string; text: string; label: string }> = {
  INGEDIEND: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Ingediend' },
  TER_GOEDKEURING: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Ter goedkeuring' },
  GOEDGEKEURD: { bg: 'bg-green-100', text: 'text-green-700', label: 'Goedgekeurd' },
  BETWIST: { bg: 'bg-red-100', text: 'text-red-700', label: 'Betwist' },
};

export function WinkelDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useDashboardSocket();
  const isEigenaar = isOwnerRole(user?.role);

  // Welcome banner after onboarding completion
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(() =>
    localStorage.getItem('cbridge_onboarding_done') === '1'
  );

  useEffect(() => {
    if (showWelcomeBanner) {
      localStorage.removeItem('cbridge_onboarding_done');
    }
  }, [showWelcomeBanner]);
  const [filterType, setFilterType] = useState<'winkel' | 'eigen' | 'collega'>(isEigenaar ? 'winkel' : 'eigen');
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [showWidgetConfig, setShowWidgetConfig] = useState(false);
  const { data: widgetConfig } = useDashboardConfig();
  const isWidgetVisible = (id: string) => {
    if (!widgetConfig?.widgetLayout) return true;
    const w = (widgetConfig.widgetLayout as Array<{ widgetId: string; zichtbaar: boolean }>).find(w => w.widgetId === id);
    return w ? w.zichtbaar : true;
  };

  const queryParams = new URLSearchParams({ filter: filterType });
  if (filterType === 'collega' && filterUserId) queryParams.set('userId', filterUserId);

  const { data, isLoading } = useQuery({
    queryKey: ['winkel-dashboard', filterType, filterUserId],
    queryFn: () => apiFetch<DashboardData>(`/api/buitendienst/winkel-dashboard?${queryParams}`),
  });

  const { data: voorraadData } = useQuery({
    queryKey: ['voorraad-dashboard'],
    queryFn: () => apiFetch<{
      items: Array<{ id: string; naam: string; huidigeVoorraad: number; minimumVoorraad: number; eenheid: string; categorie: string }>;
      kpis: { totaalItems: number; onderMinimum: number; totaleWaarde: number; laatsteInventaris: string | null };
    }>('/api/logistiek/voorraad'),
  });

  const kpis = data?.kpis;
  const alerts = data?.alerts || [];
  const dossiers = data?.dossiers || [];
  const collega = data?.collega || [];
  const { data: bestellingenData } = useQuery({
    queryKey: ['bestellingen-dashboard'],
    queryFn: () => apiFetch<{ bestellingen: any[]; kpis: { openstaand: number; totaalOpenBedrag: number } }>('/api/logistiek/bestellingen'),
  });

  const voorraadAlerts = voorraadData?.items.filter(i => i.huidigeVoorraad < i.minimumVoorraad) || [];
  const voorraadKpis = voorraadData?.kpis;
  const bestellingenKpis = bestellingenData?.kpis;

  const columns: Column<DashboardData['dossiers'][0]>[] = [
    { key: 'dossierNummer', label: 'Bestelbonnr', sortable: true, render: (r) => (
      <span className="font-medium text-gray-900">{r.dossierNummer}</span>
    )},
    { key: 'klantNaam', label: 'Klant', sortable: true, render: (r) => (
      <span className="text-sm text-gray-900">{r.klantNaam}</span>
    )},
    { key: 'leverDatum', label: 'Leverdatum', sortable: true, render: (r) => (
      <span className="text-sm text-gray-600">{formatLeverDatum(r.leverDatum)}</span>
    )},
    { key: 'status', label: 'Status', render: (r) => {
      const active = r.trajectstappen?.find((s: any) => s.status === 'active');
      if (!active) return <span className="text-xs text-gray-400">{r.status}</span>;
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-gold/10 text-gold">
          {active.template.icon} {parseNaam(active.template.naam)}
        </span>
      );
    }},
    { key: 'apparaten', label: 'Apparaten', render: (r) => {
      if (r.apparatenTotaal === 0) return <span className="text-xs text-gray-400">—</span>;
      const compleet = r.apparatenBinnen === r.apparatenTotaal;
      return (
        <span className={`text-sm font-medium ${compleet ? 'text-green-600' : 'text-amber-600'}`}>
          {r.apparatenBinnen}/{r.apparatenTotaal} binnen
        </span>
      );
    }},
    { key: 'vorderingsstaat', label: 'Vorderingsstaat', render: (r) => {
      if (!r.vorderingsstaatStatus) return <span className="text-xs text-gray-400">—</span>;
      const badge = statusBadge[r.vorderingsstaatStatus] || statusBadge.INGEDIEND;
      return (
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${badge.bg} ${badge.text}`}>
          {badge.label}
        </span>
      );
    }},
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Welcome banner after onboarding */}
      {showWelcomeBanner && (
        <div className="bg-gradient-to-r from-brand-secondary to-[#3d3d3d] rounded-xl p-5 text-white relative overflow-hidden">
          <button
            onClick={() => setShowWelcomeBanner(false)}
            className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-brand rounded-xl flex items-center justify-center shrink-0">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg mb-1">Welkom bij Kordaro! 🎉</h3>
              <p className="text-white/70 text-sm mb-4">Uw winkel is actief. Hier zijn 3 dingen om nu te doen:</p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('/winkel')}
                  className="flex items-center gap-2 bg-brand hover:bg-[#b8973d] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  <FolderOpen size={14} />
                  Dossiers aanmaken
                </button>
                <button
                  onClick={() => navigate('/onboarding/setup')}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Team uitnodigen
                  <ArrowRight size={14} />
                </button>
                <button
                  onClick={() => navigate('/settings')}
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Instellingen bekijken
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header + Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welkom{user ? `, ${user.firstName}` : ''}
          </h1>
          <p className="text-gray-500 mt-1">{formatDatum(new Date())} — Winkel overzicht</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={filterType === 'collega' ? `collega:${filterUserId}` : filterType}
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'winkel' || val === 'eigen') {
                  setFilterType(val);
                  setFilterUserId('');
                } else if (val.startsWith('collega:')) {
                  setFilterType('collega');
                  setFilterUserId(val.replace('collega:', ''));
                }
              }}
              className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold"
            >
              <option value="winkel">Hele winkel</option>
              <option value="eigen">Mijn dossiers</option>
              {collega.filter(c => c.id !== user?.id).map(c => (
                <option key={c.id} value={`collega:${c.id}`}>{c.firstName} {c.lastName}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          <button
            onClick={() => navigate('/import')}
            className="bg-gold text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#A88B3D] transition-colors"
          >
            Import
          </button>
          <button
            onClick={async () => {
              const data = await apiFetch<{ rows: Record<string, unknown>[]; filename: string }>('/api/dashboard/export/excel/dossiers');
              const XLSX = await import('xlsx');
              const ws = XLSX.utils.json_to_sheet(data.rows);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Dossiers');
              XLSX.writeFile(wb, `${data.filename}.xlsx`);
            }}
            className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            title="Export Excel"
          >
            <Download size={16} />
          </button>
          <button
            onClick={() => setShowWidgetConfig(true)}
            className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            title="Personaliseer"
          >
            <Settings2 size={16} />
          </button>
        </div>
      </div>

      <WidgetConfigModal open={showWidgetConfig} onClose={() => setShowWidgetConfig(false)} />

      {/* Ochtend Briefing */}
      <BriefingCard />

      {/* KPI Cards */}
      <div className={clsx('grid gap-4', isEigenaar ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3')}>
        <KPITile label="Actieve Dossiers" waarde={kpis?.actieveDossiers ?? '—'} icon={<FolderOpen size={20} />} accentKleur="var(--brand-primary)" />
        <KPITile label="Leveringen deze week" waarde={kpis?.leveringenDezeWeek ?? '—'} icon={<Truck size={20} />} accentKleur="#3B82F6" />
        {isEigenaar && (
          <KPITile label="Openstaande vorderingsstaten" waarde={kpis?.openVorderingsstaten ?? '—'} icon={<FileCheck size={20} />} accentKleur="#D97706" />
        )}
        <KPITile label="Apparaten te laat" waarde={kpis?.apparatenTeLaat ?? '—'} icon={<Clock size={20} />} accentKleur="#DC2626" />
      </div>

      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column — Main content (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dossier Table */}
          {isWidgetVisible('dossier_lijst') && <div>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Dossiers</h2>
            <Card padding={false}>
              {isLoading ? (
                <div className="p-12 text-center">
                  <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  data={dossiers}
                  getRowKey={(r) => r.id}
                  onRowClick={(r) => navigate(`/winkel/dossier/${r.id}`)}
                  emptyMessage="Geen dossiers gevonden"
                />
              )}
            </Card>
          </div>}
        </div>

        {/* Right Column — Sidebar widgets (1/3 width) */}
        <div className="space-y-4">
          {/* Alert Panel */}
          {isWidgetVisible('alerts') && <AlertPanel />}

          {/* Urgente Alerts (dossier-specifiek) */}
          {alerts.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-2">
              <h3 className="text-sm font-semibold text-gray-700">Aandachtspunten</h3>
              {alerts.map(alert => (
                <div
                  key={alert.id}
                  onClick={() => navigate(`/winkel/dossier/${alert.id}`)}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors text-sm ${
                    alert.type === 'URGENT'
                      ? 'bg-red-50 hover:bg-red-100'
                      : 'bg-amber-50 hover:bg-amber-100'
                  }`}
                >
                  {alert.type === 'URGENT'
                    ? <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                    : <AlertCircle size={14} className="text-amber-500 flex-shrink-0" />
                  }
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-xs truncate">{alert.dossierNummer} — {alert.klantNaam}</p>
                    <p className="text-[11px] text-gray-500 truncate">{alert.reden}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quick Stats */}
          {((bestellingenKpis && bestellingenKpis.openstaand > 0) || (voorraadKpis && voorraadKpis.onderMinimum > 0)) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Logistiek</h3>
              {bestellingenKpis && bestellingenKpis.openstaand > 0 && (
                <div onClick={() => navigate('/logistiek/bestellingen')} className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg p-2 -mx-2 transition-colors">
                  <div className="flex items-center gap-2">
                    <ShoppingCart size={16} className="text-amber-500" />
                    <span className="text-sm text-gray-700">Open bestellingen</span>
                  </div>
                  <span className="text-sm font-semibold text-amber-600">{bestellingenKpis.openstaand}</span>
                </div>
              )}
              {voorraadKpis && voorraadKpis.onderMinimum > 0 && (
                <div onClick={() => navigate('/logistiek/besteladvies')} className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg p-2 -mx-2 transition-colors">
                  <div className="flex items-center gap-2">
                    <Warehouse size={16} className="text-red-500" />
                    <span className="text-sm text-gray-700">Items onder minimum</span>
                  </div>
                  <span className="text-sm font-semibold text-red-600">{voorraadKpis.onderMinimum}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
